// Device-linking driver (Layer CH6c). Runs the history transfer end to end over
// the CH6a relay and the CH6b secure channel: pubkey exchange → SAS gate →
// approval → history stream. It is written against small interfaces so the real
// wasm LinkHandshake/LinkChannel (mls-wasm) satisfy them directly, and the unit
// test drives both roles with in-memory fakes. See docs/specs/chat-device-linking.md.

import type { StoredMessage } from './chatDb';
import { chunkBySize, decodeLink, encodeLink, LINK_VERSION, type LinkConvMeta, type LinkQr } from './linkWire';

// ── injected capabilities ───────────────────────────────────────────────────

/** One side of the relay, already bound to a rendezvous + side. `recv`
 * long-polls; a null result means "poll timed out, call again". */
export interface LinkRelay {
  send(frame: Uint8Array): Promise<void>;
  recv(): Promise<Uint8Array | null>;
}

/** The CH6b channel. The wasm `LinkChannel` satisfies this structurally. */
export interface LinkCrypto {
  readonly sas: string;
  readonly ready: boolean;
  approve(): Uint8Array;
  seal(plaintext: Uint8Array): Uint8Array;
  open(frame: Uint8Array): Uint8Array | undefined;
}

/** The CH6b handshake. The wasm `LinkHandshake` satisfies this structurally.
 * `derive` consumes the handshake, so read `publicKey` first. */
export interface LinkDeriver {
  readonly publicKey: Uint8Array;
  derive(
    isPrimary: boolean,
    peerPublic: Uint8Array,
    rendezvousId: Uint8Array,
    accountId: Uint8Array,
    version: number,
  ): LinkCrypto;
}

/** The Primary's read side: what history it holds. */
export interface HistorySource {
  listConvs(): Promise<LinkConvMeta[]>;
  messagesAfter(convId: string, afterSeq: number): Promise<StoredMessage[]>;
}

/** The New device's write side: where transferred history lands. */
export interface HistorySink {
  /** convId → highest seq already held (drives incremental re-links). */
  cursor(): Promise<Record<string, number>>;
  applyConv(meta: LinkConvMeta): Promise<void>;
  applyMessages(convId: string, messages: StoredMessage[]): Promise<void>;
}

// ── errors ──────────────────────────────────────────────────────────────────

export class LinkAbortedError extends Error {
  constructor() {
    super('link aborted — the safety numbers did not match, or it was declined');
  }
}
export class LinkProtocolError extends Error {}
export class LinkTimeoutError extends Error {
  constructor() {
    super('link timed out waiting for the other device');
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────

const te = new TextEncoder();

/** Block until a frame arrives, re-polling across the relay's long-poll
 * timeouts, up to a generous deadline. */
async function recvFrame(relay: LinkRelay, deadlineMs = 5 * 60_000): Promise<Uint8Array> {
  const deadline = Date.now() + deadlineMs;
  for (;;) {
    const f = await relay.recv();
    if (f) return f;
    if (Date.now() >= deadline) throw new LinkTimeoutError();
  }
}

async function sealSend(relay: LinkRelay, channel: LinkCrypto, plaintext: Uint8Array): Promise<void> {
  await relay.send(channel.seal(plaintext));
}

/** Open a frame that must be payload (not the peer's approval). */
function sealOpen(channel: LinkCrypto, frame: Uint8Array): Uint8Array {
  const pt = channel.open(frame);
  if (pt === undefined) throw new LinkProtocolError('unexpected approval frame mid-stream');
  return pt;
}

/** Exchange the two approval frames and confirm both sides are ready. Each side
 * sends its own approval, then opens the peer's. */
async function exchangeApproval(relay: LinkRelay, channel: LinkCrypto): Promise<void> {
  await relay.send(channel.approve());
  const peerApproval = await recvFrame(relay);
  if (channel.open(peerApproval) !== undefined) {
    throw new LinkProtocolError('expected the peer approval frame');
  }
  if (!channel.ready) throw new LinkProtocolError('channel not ready after approval exchange');
}

// ── roles ───────────────────────────────────────────────────────────────────

export interface CompanionOpts {
  relay: LinkRelay;
  /** A fresh handshake; its public key is sent to the Primary. */
  handshake: LinkDeriver;
  /** THIS device's authenticated user id — bound into the transcript. */
  accountId: string;
  /** The scanned QR (rendezvous id + Primary's public key + version). */
  qr: LinkQr;
  sink: HistorySink;
  /** Show the SAS; resolve true when the human confirms it matches. */
  confirmSas: (sas: string) => Promise<boolean>;
  onProgress?: (received: number, targetHint: number) => void;
}

/** The New device: scan → derive → SAS → approve → receive history. */
export async function runCompanion(o: CompanionOpts): Promise<{ received: number }> {
  const myPublic = o.handshake.publicKey; // read before derive consumes it
  const channel = o.handshake.derive(false, fromB64(o.qr.pub), te.encode(o.qr.rid), te.encode(o.accountId), o.qr.v);

  // Send our public key (plain — pre-channel) so the Primary can derive.
  await o.relay.send(myPublic);

  if (!(await o.confirmSas(channel.sas))) throw new LinkAbortedError();
  await exchangeApproval(o.relay, channel);

  await sealSend(o.relay, channel, encodeLink({ v: LINK_VERSION, t: 'hello', cursor: await o.sink.cursor() }));

  let received = 0;
  let target = 0;
  for (;;) {
    const msg = decodeLink(sealOpen(channel, await recvFrame(o.relay)));
    if (msg.t === 'manifest') {
      target = msg.convs.reduce((n, c) => n + c.maxSeq, 0);
      for (const c of msg.convs) await o.sink.applyConv(c);
    } else if (msg.t === 'chunk') {
      if (msg.messages.length) await o.sink.applyMessages(msg.convId, msg.messages);
      received += msg.messages.length;
      o.onProgress?.(received, target);
    } else if (msg.t === 'done') {
      break;
    } else {
      throw new LinkProtocolError(`unexpected ${msg.t} before done`);
    }
  }

  await sealSend(o.relay, channel, encodeLink({ v: LINK_VERSION, t: 'complete', received }));
  return { received };
}

export interface PrimaryOpts {
  relay: LinkRelay;
  /** The SAME handshake whose public key is in the QR. */
  handshake: LinkDeriver;
  accountId: string;
  /** Rendezvous id and version — the values also encoded in the QR. */
  rendezvousId: string;
  version: number;
  source: HistorySource;
  confirmSas: (sas: string) => Promise<boolean>;
  onProgress?: (sent: number, total: number) => void;
}

/** The device with history: receive companion key → derive → SAS → approve →
 * stream history (incrementally, honoring the companion's cursor). */
export async function runPrimary(o: PrimaryOpts): Promise<{ sent: number }> {
  const companionPublic = await recvFrame(o.relay);
  const channel = o.handshake.derive(
    true,
    companionPublic,
    te.encode(o.rendezvousId),
    te.encode(o.accountId),
    o.version,
  );

  if (!(await o.confirmSas(channel.sas))) throw new LinkAbortedError();
  await exchangeApproval(o.relay, channel);

  const hello = decodeLink(sealOpen(channel, await recvFrame(o.relay)));
  if (hello.t !== 'hello') throw new LinkProtocolError('expected hello');
  const cursor = hello.cursor;

  const convs = await o.source.listConvs();
  const total = convs.reduce((n, c) => n + Math.max(0, c.maxSeq - (cursor[c.id] ?? 0)), 0);
  await sealSend(o.relay, channel, encodeLink({ v: LINK_VERSION, t: 'manifest', convs }));

  let sent = 0;
  for (const c of convs) {
    const msgs = await o.source.messagesAfter(c.id, cursor[c.id] ?? 0);
    for (const chunk of chunkBySize(c.id, msgs)) {
      await sealSend(o.relay, channel, encodeLink(chunk));
      sent += chunk.messages.length;
      o.onProgress?.(sent, total);
    }
  }

  await sealSend(o.relay, channel, encodeLink({ v: LINK_VERSION, t: 'done' }));
  const complete = decodeLink(sealOpen(channel, await recvFrame(o.relay)));
  if (complete.t !== 'complete') throw new LinkProtocolError('expected complete');
  return { sent };
}

// Local base64 (avoids a cycle with chatApi in tests; identical behavior).
function fromB64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
